import React from "react";
import { Link } from "react-router-dom";
import heroImg from "../images/hero.svg";
import appStoreBadge from "../images/Download_on_the_App_Store_Badge.svg";
import { useTranslation } from "../contexts/LanguageContext";

const Hero = () => {
  const { t } = useTranslation();

  const APPLE_APP_STORE_URL = "https://apps.apple.com/us/app/easysubs/id6753924132";

  return (
    <div className="hero bg-gradient-to-br from-blue-50 to-white" id="hero">
      <div
        className="m-auto overflow-hidden mx-4 mt-8 lg:mt-4 p-2 md:p-12 h-5/6"
        data-aos="zoom-in"
      >
        <div className="flex flex-col lg:flex-row py-8 justify-between text-center lg:text-left">
          <div
            className="lg:w-1/2 flex flex-col justify-center"
            data-aos="zoom-in"
            data-aos-delay="200"
          >
            <h1 className="mb-5 md:text-6xl text-4xl font-bold text-blue-900 leading-tight">
              {t('hero.title')}
              <span className="text-orange-600 block">{t('hero.subtitle')}</span>
            </h1>
            <div className="text-xl lg:text-2xl font-semibold tracking-tight mb-5 text-gray-600">
              {t('hero.description')}
            </div>

            {/* Feature Highlights */}
            <div className="mb-6 grid grid-cols-2 gap-4">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-gray-700">{t('hero.feature1')}</span>
              </div>
              <div className="flex items-center">
                <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-gray-700">{t('hero.feature2')}</span>
              </div>
              <div className="flex items-center">
                <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-gray-700">{t('hero.feature3')}</span>
              </div>
              <div className="flex items-center">
                <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                <span className="text-gray-700">{t('hero.feature4')}</span>
              </div>
            </div>

            {/* Two buttons: Sign Up Free + App Store Badge - same big dimensions */}
            <div className="mb-4 flex flex-col sm:flex-row gap-4">
              <Link
                to="/demo"
                className="text-white bg-orange-600 hover:bg-orange-700 inline-flex items-center justify-center w-full px-8 py-4 text-lg font-semibold shadow-xl rounded-2xl transition-all duration-300 transform hover:-translate-y-1"
              >
                {t('hero.signupFree')}
                <svg
                  className="w-5 h-5 ml-2"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </Link>

              {/* App Store Badge - now larger, same padding and dimensions as signup button */}
              <a
                href={APPLE_APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-black hover:bg-gray-900 inline-flex items-center justify-center w-full px-8 py-4 shadow-xl rounded-2xl transition-all duration-300 transform hover:-translate-y-1"
                aria-label="Download on the App Store"
              >
                <img
                  src={appStoreBadge}
                  alt="Download on the App Store"
                  className="h-10 md:h-12 w-auto"
                />
              </a>
            </div>
          </div>

          <div
            className="flex lg:justify-end w-full lg:w-1/2"
            data-aos="fade-up"
            data-aos-delay="700"
          >
            <div className="relative">
              <img
                alt="EasySubs Dashboard Preview"
                className="rounded-2xl shadow-2xl w-full max-w-2xl"
                src={heroImg}
              />
              <div className="absolute -top-4 -right-4 bg-white p-4 rounded-xl shadow-lg">
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                  <span className="text-sm font-semibold">Active Subscriptions</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Hero;